import {
    BaseType,
    BuildComponentType,
    CardinalDirection, ColorEnum, ElevatorType, HazardousState, LevelType,
    Material, RiskConstructionType,
    RoofShape,
    RoomType,
    UtilityCategory,
    WindowPaneType
} from "./Enums";
import {BuildingType} from "./BuildingTypeEnums";
import {ComponentType} from "react";
import {AnyGeometryShape} from "./GeometryShapes";

export interface Building {
    key: string;
    summary: BuildingSummary;
    model: BuildingModel;
}

export interface BuildingSummary {
    key: string;
    buildingType?: BuildingType;
    name?: string;
    coordinates?: CoordinatesModel;
    locationCategories?: LocationCategories;
    coverPhoto?: PhotoModel;
    groupTags?: { [key: string]: GroupTag };
    buildingInfo?: BuildingInfo;
}

export interface BuildingModel {
    key: string;
    structure?: BuildingStructure;
    documents?: { [key: string]: DocumentModel };
    contacts?: { [key: string]: ContactModel}
    mapImage?: PhotoModel;
}

export interface BuildingStructure {
    components?: { [key: string]: BaseComponent};
    rooms?: { [key: string]: RoomModel };
    levels?: { [key: string]: LevelModel };
    types?: ComponentTypes;
}

export interface ComponentTypes {
    surfaceTypes: { [key: string]: SurfaceType };
    windowTypes: { [key: string]: WindowType };
    doorTypes: { [key: string]: DoorType };
}

export interface SurfaceType {
    key: string;
    interiorType: ComponentType;
    material: ValueWithMetadata<Material>;
    color: ValueWithMetadata<ColorModel>;
}

export interface WindowType {
    key: string;
    material: ValueWithMetadata<Material>;
    color: ValueWithMetadata<ColorModel>;
    paneType: ValueWithMetadata<WindowPaneType>;
    areaShapes: ValueWithMetadata<AnyGeometryShape>;
}

export interface DoorType {
    key: string;
    material: ValueWithMetadata<Material>;
    color: ValueWithMetadata<ColorModel>;
    areaShapes: ValueWithMetadata<AnyGeometryShape>;
    doorCategory: ValueWithMetadata<DoorCategory>;
}

export enum DoorCategory {
    interior_door = "interior_door",
    exterior_door = "exterior_door"
}

export enum ValueStatus {
    notSet = "notSet",
    inaccessible = "inaccessible",
    set = "set"
}

export interface ValueWithMetadata<T> {
    readonly value: T | null;
    readonly updatedWhen: string | null;
    readonly updatedBy: string | null;
    readonly status: ValueStatus;
}

export interface GroupTag {
    key: string;
    tag: string;
}

export interface canHavePhotos {
    photos?: { [key: string]: PhotoModel };
}

export interface canHaveLabel {
    label?: string;
}

export interface canHaveCondition {
    condition?: number;
}

export interface canHaveComponentType {
    specificType?: BuildComponentType;
}

export interface BaseComponent {

    key: string;
    type: BaseType;
    createdBy: string;
    createdWhen: string;
    comment: ValueWithMetadata<string>;
    photos?: { [key: string]: PhotoModel };
    specificType?: BuildComponentType;
    condition?: ValueWithMetadata<number>;
    buildingRisks?: ValueWithMetadata<RiskConstruction>;
    material?: ValueWithMetadata<Material[]>;
    color?: ValueWithMetadata<ColorModel>;
    areaShapes?: ValueWithMetadata<AnyGeometryShape[]>;
    cardinalDirection?: ValueWithMetadata<CardinalDirection>;
    depth?: ValueWithMetadata<number>;
    insulation?: ValueWithMetadata<number>;
    label?: string;
    wallCount?: ValueWithMetadata<number>;
    hazardous?: ValueWithMetadata<HazardousModel>;
    canHaveWindows?: Boolean;
    canHaveDoors?: Boolean;
    roofSlope?: ValueWithMetadata<number>;
    roofShape?: ValueWithMetadata<RoofShape>;
    parentRoomKeys?: ValueWithMetadata<string[]>;
    typeKey?: ValueWithMetadata<string>;
    //lockMechanism?: ValueWithMetadata<LockMechanismModel>;
    roomKey?: string;
    utilityCategory?: UtilityCategory;
    elevatorType?: ValueWithMetadata<ElevatorType>;

    displayName(): string;
    totalArea(): number|null;
}

export interface IsRoom extends canHaveLabel, canHavePhotos {
    key: string;
    number?: string;
    levelKey?: string;
    location?: BlueprintLocation;
    type?: RoomType;
    ceilingHeight: ValueWithMetadata<number>;
}

export interface DocumentModel {
    fileType?: string;
    key: string;
    name: string;
    uploadedAt?: string;
    url?: string;
}

export interface BuildingWithCoverUrl extends BuildingModel {
    coverPhotoUrl?: string;
}

export interface ColorModel {
    colorEnum?: ColorEnum;
    code?: string;
}
export interface PhotoModel {
    key: string;
    author: string;
    date: string;
    width: number;
    height: number;
    storagePath: string;
    fullSizeUrl: string;
    mediumSizeUrl: string;
    smallSizeUrl: string;
}

export interface HazardousModel {
    asbestos?: HazardousState;
    pcb?: HazardousState;
    led?: HazardousState;
    arsenic?: HazardousState;
    blueConcrete?: HazardousState;
    radon?: HazardousState;
}

export interface CoordinatesModel {
    latitude: number;
    longitude: number;
}

export interface BlueprintLocation {
    planKey: string;
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface RoomLevel {
    levelKey: string;
    level: number;
}

export interface RoomModel extends IsRoom, canHaveLabel, canHavePhotos {}

export interface LevelModel {
    key: string;
    name?: string;
    number: number;
    ceilingHeight?: number;
    type?: LevelType;
    planImage?: PhotoModel;
}

export interface RiskConstruction {
    risks?: RiskConstructionType[];
    customRisk?: string;
}

export interface LocationCategories {
    category1?: LocationCategory;
    category2?: LocationCategory;
    category3?: LocationCategory;
    category4?: LocationCategory;
    category5?: LocationCategory;
}

export interface LocationCategory {
    name: string;
    key: String;
}

export interface BuildingInfo {
    keyInstructions?: string;
    street?: string;
    postalCode?: string;
    city?: string;
    municipality?: string;
    other?: string;
    hasPoorReception?: boolean;
}

export interface ContactModel {
    key: string;
    name: string;
    role?: string;
    company?: string;
    phoneNumber?: string;
    email?: string;
    info?: string;
}
